import Constants from "expo-constants";
import type { Content, UserAttributes } from "@intercom/intercom-react-native";
import type { UserFieldsFragment } from "../../__generated__/operations/graphql";
import type { TrackingEventName } from "../tracking";

/**
 * Contract shared by the native (intercom.ts) and web (intercom.web.ts) hooks.
 * TypeScript only type-checks imports against the native file, so both
 * implementations must declare this type for divergences to be caught.
 */
export type IntercomHook = {
    login: (user: UserFieldsFragment) => Promise<void>;
    loginUnidentifiedUser: () => Promise<boolean>;
    logout: () => Promise<void>;
    presentIntercom: () => Promise<boolean>;
    presentMessageComposer: () => Promise<boolean>;
    presentContent: (content: Content) => Promise<boolean>;
    presentArticle: (articleId: ArticleId) => Promise<boolean>;
    presentArticleId: (articleId: string) => Promise<boolean>;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    logEvent: (eventName: TrackingEventName, params?: { [key: string]: any }) => Promise<boolean>;
    updateUser: (user: UserAttributes) => Promise<boolean>;
};

export type ArticleId =
    | "CreateMissionEarningType.time_sheets"
    | "SalaryProjection"
    | "InvoicedSalary"
    | "IJMGroupClient"
    | "IJMGroupWorker"
    | "IJMTalentProfile"
    | "MyPayCheckInfo"
    | "WPOneInfo"
    | "WorkingTimeTypes"
    | "HowToInviteClient"
    | "LPPSelection";

/**
 * Indirection to match key to article ID in Intercom
 */
const ARTICLES: Record<ArticleId | "default", string> = {
    "CreateMissionEarningType.time_sheets": "8708309",
    SalaryProjection: "9319123",
    InvoicedSalary: "8811204",
    IJMGroupClient: "10430244",
    IJMGroupWorker: "10436709",
    IJMTalentProfile: "10721947",
    MyPayCheckInfo: "11018443",
    WPOneInfo: "15177126",
    WorkingTimeTypes: "11051899",
    HowToInviteClient: "11695705",
    LPPSelection: "11687909",
    default: "8717817",
};

export const getArticleId = (articleId: ArticleId): string => {
    return ARTICLES[articleId] || ARTICLES.default;
};

export const isIntercomEnabled = (): boolean => {
    return !!Constants.expoConfig?.extra?.intercomAppId;
};
